import { z } from 'zod';
import { isValidTimeZone, parseHHMM } from './schedule.js';
/**
 * Backup policy specification (stored immutably per revision in policy_revisions.spec).
 *
 * Selection matching precedence (implemented by agent/internal/selection, documented in docs/policies.md):
 *  1. Candidate paths come from include roots: expanded `includePaths` and, in `profiles` mode, the
 *     chosen known folders of each selected local user profile. Environment variables are expanded per
 *     platform (%VAR% on Windows, $VAR / ${VAR} on Linux).
 *  2. Built-in exclusions always apply (pagefile/hiberfil/swapfile, System Volume Information,
 *     $Recycle.Bin, the agent's own state/cache directories, /proc, /sys, /dev, /run).
 *  3. Among includePaths/excludePaths, the MOST SPECIFIC (longest) matching path wins; an include path
 *     nested inside an excluded path is therefore included.
 *  4. excludeGlobs remove matching files and directories below an include root (never the root itself).
 *  5. File-only filters: includeGlobs (if set, a file must match one), extension include/exclude,
 *     min/max size. Directories are always traversed unless excluded by rules 2-4.
 *  6. Symlinks and junctions are recorded as links and never followed.
 *  Glob syntax: `*` (no separator), `**` (any depth), `?`, `[abc]`; case-insensitive on Windows.
 */
const hhmm = z.string().refine((s) => {
    try {
        parseHHMM(s);
        return true;
    }
    catch {
        return false;
    }
}, 'expected HH:MM (24h)');
export const scheduleSchema = z
    .object({
    kind: z.enum(['daily', 'weekly', 'interval', 'continuous']),
    times: z.array(hhmm).max(24).optional(),
    days: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    everyMinutes: z.number().int().min(15).max(1440).optional(),
    windowStart: hhmm.optional(),
    windowEnd: hhmm.optional(),
    /** Continuous only: quiet period after the last change in the watched folder before a run starts. */
    debounceSeconds: z.number().int().min(1).max(3600).optional(),
    /** Continuous only: how long a run may wait for Wi-Fi or charge before it goes anyway. */
    maxDelayMinutes: z.number().int().min(1).max(1440).optional(),
    /** Continuous only: full re-scan interval, so a missed watcher event cannot hide a file forever. */
    safetyNetMinutes: z.number().int().min(15).max(1440).optional(),
    timezone: z.string().refine((tz) => tz === 'device' || isValidTimeZone(tz), 'unknown IANA time zone'),
    jitterMinutes: z.number().int().min(0).max(120).default(10),
    catchUp: z.enum(['once', 'none']).default('once'),
})
    .superRefine((s, ctx) => {
    if ((s.kind === 'daily' || s.kind === 'weekly') && (!s.times || s.times.length === 0))
        ctx.addIssue({ code: 'custom', path: ['times'], message: 'at least one time is required' });
    if (s.kind === 'weekly' && (!s.days || s.days.length === 0))
        ctx.addIssue({ code: 'custom', path: ['days'], message: 'weekly schedules need at least one day' });
    if (s.kind === 'interval' && !s.everyMinutes)
        ctx.addIssue({ code: 'custom', path: ['everyMinutes'], message: 'interval schedules need everyMinutes' });
    if (s.kind === 'continuous') {
        // Slot fields would be silently ignored by both schedulers, so reject them instead.
        for (const f of ['times', 'days', 'everyMinutes', 'windowStart', 'windowEnd'])
            if (s[f] !== undefined)
                ctx.addIssue({ code: 'custom', path: [f], message: 'continuous schedules have no time slots' });
    }
    else {
        for (const f of ['debounceSeconds', 'maxDelayMinutes', 'safetyNetMinutes'])
            if (s[f] !== undefined)
                ctx.addIssue({ code: 'custom', path: [f], message: 'only continuous schedules use this' });
    }
});
export const retentionSchema = z.object({
    /** Keep every snapshot from the last N days (intraday). 0 disables. */
    intradayDays: z.number().int().min(0).max(365).default(0),
    daily: z.number().int().min(0).max(3650).default(30),
    weekly: z.number().int().min(0).max(520).default(0),
    monthly: z.number().int().min(0).max(240).default(0),
    yearly: z.number().int().min(0).max(100).default(0),
    /** Always keep at least this many most recent snapshots regardless of age. */
    keepLast: z.number().int().min(1).max(1000).default(3),
});
const pathString = z
    .string()
    .min(1)
    .max(4096)
    .refine((p) => !p.includes('\0'), 'NUL not allowed');
export const pathRuleSchema = z.object({
    path: pathString,
    /** Limit the rule to one platform; omitted means both. */
    os: z.enum(['windows', 'linux']).optional(),
});
export const selectionSchema = z.object({
    /**
     * `mobile` selects the ZeroGap app's own "My Files" container on a phone. The absolute path differs per
     * install and per platform, so the device resolves it; nothing else about the phone is ever selected.
     */
    mode: z.enum(['profiles', 'paths', 'mobile']),
    /** Known folders per user profile in `profiles` mode. */
    profileFolders: z.array(z.enum(['Documents', 'Desktop', 'Pictures', 'Downloads', 'Music', 'Videos'])).default([]),
    /** Which local user profiles: "all" or explicit usernames. */
    profiles: z.union([z.literal('all'), z.array(z.string().min(1).max(256)).min(1)]).default('all'),
    excludeProfiles: z.array(z.string().min(1).max(256)).default([]),
    includePaths: z.array(pathRuleSchema).max(200).default([]),
    excludePaths: z.array(pathRuleSchema).max(500).default([]),
    excludeGlobs: z.array(z.string().min(1).max(512)).max(500).default([]),
    includeGlobs: z.array(z.string().min(1).max(512)).max(200).default([]),
    includeExtensions: z.array(z.string().regex(/^[A-Za-z0-9_+-]{1,32}$/)).max(200).default([]),
    excludeExtensions: z.array(z.string().regex(/^[A-Za-z0-9_+-]{1,32}$/)).max(200).default([]),
    minFileSizeBytes: z.number().int().min(0).optional(),
    maxFileSizeBytes: z.number().int().min(1).optional(),
});
export const limitsSchema = z.object({
    uploadConcurrency: z.number().int().min(1).max(16).default(2),
    bandwidthKbps: z.number().int().min(0).max(10_000_000).default(0), // 0 = unlimited
    cpuPriority: z.enum(['low', 'normal']).default('low'),
    maxDurationMinutes: z.number().int().min(10).max(7 * 24 * 60).default(12 * 60),
    onBattery: z.enum(['run', 'skip']).default('skip'),
    meteredNetwork: z.enum(['run', 'skip']).default('skip'),
    maxMemoryMB: z.number().int().min(64).max(8192).default(512),
    cacheMaxMB: z.number().int().min(64).max(100_000).default(2048),
    /** Backup window: runs start only inside it and are stopped (resumably) when it ends. Optional. */
    windowStart: hhmm.optional(),
    windowEnd: hhmm.optional(),
});
export const policySpecSchema = z.object({
    selection: selectionSchema,
    schedule: scheduleSchema,
    retention: retentionSchema,
    limits: limitsSchema.default(limitsSchema.parse({})),
    compression: z.enum(['default', 'fast', 'none']).default('default'),
    /** Windows VSS: required = fail if unavailable; preferred = visible degraded mode; off = never. */
    vss: z.enum(['required', 'preferred', 'off']).default('preferred'),
    verification: z
        .object({
        sampleEveryDays: z.number().int().min(0).max(365).default(7),
        sampleChunks: z.number().int().min(1).max(1000).default(32),
        restoreDrillEveryDays: z.number().int().min(0).max(365).default(30),
    })
        .default({ sampleEveryDays: 7, sampleChunks: 32, restoreDrillEveryDays: 30 }),
    alerting: z
        .object({
        /** Maximum acceptable age of the last successful recovery point before the device is "unprotected". */
        maxAgeHours: z.number().int().min(1).max(24 * 90).default(36),
    })
        .default({ maxAgeHours: 36 }),
    retry: z
        .object({
        maxRetries: z.number().int().min(0).max(10).default(3),
        retryDelayMinutes: z.number().int().min(1).max(24 * 60).default(15),
    })
        .default({ maxRetries: 3, retryDelayMinutes: 15 }),
});
/** An include path must be absolute (drive letter, UNC share, or "/") or start with an environment variable. */
export function looksAbsolutePath(path) {
    const p = path.trim();
    return /^[A-Za-z]:[\\/]/.test(p) || p.startsWith('/') || p.startsWith('\\\\') || /^(%[A-Za-z_]\w*%|\$\{?[A-Za-z_]\w*\}?)/.test(p);
}
/** A policy spec is "complete" (activatable) when it selects something concrete. */
export function policyCompleteness(spec) {
    const problems = [];
    if (spec.selection.mode === 'profiles' && spec.selection.profileFolders.length === 0)
        problems.push('Choose at least one profile folder (e.g. Documents).');
    if (spec.selection.mode === 'mobile' && spec.schedule.kind !== 'continuous')
        problems.push('Phones back up continuously; this plan has a schedule.');
    if (spec.selection.mode === 'paths' && spec.selection.includePaths.length === 0)
        problems.push('This policy needs at least one folder to back up. Add the business folders to protect.');
    for (const r of spec.selection.includePaths) {
        const p = r.path.trim();
        if (!looksAbsolutePath(p))
            problems.push(`Include path "${p}" must be absolute or start with an environment variable.`);
    }
    const r = spec.retention;
    if (r.intradayDays + r.daily + r.weekly + r.monthly + r.yearly === 0 && r.keepLast < 1)
        problems.push('Retention keeps nothing.');
    return { complete: problems.length === 0, problems };
}
/** Immutable built-in templates. Admins clone them; clones must be completed before activation. */
export const BUILT_IN_TEMPLATES = [
    {
        key: 'essential-workstation',
        name: 'Essential workstation',
        description: 'Documents and Desktop for all user profiles, daily at 19:00 local time, 30 daily snapshots.',
        spec: {
            selection: { mode: 'profiles', profileFolders: ['Documents', 'Desktop'], profiles: 'all' },
            schedule: { kind: 'daily', times: ['19:00'], timezone: 'device', jitterMinutes: 15, catchUp: 'once' },
            retention: { intradayDays: 0, daily: 30, weekly: 0, monthly: 0, yearly: 0, keepLast: 3 },
            alerting: { maxAgeHours: 36 },
        },
    },
    {
        key: 'business-files',
        name: 'Business files',
        description: 'Explicit business folders chosen during setup, every four hours between 08:00 and 20:00, 14 days of intraday and 30 daily snapshots.',
        spec: {
            selection: { mode: 'paths', includePaths: [] },
            schedule: {
                kind: 'interval',
                everyMinutes: 240,
                windowStart: '08:00',
                windowEnd: '20:00',
                days: [1, 2, 3, 4, 5],
                timezone: 'device',
                jitterMinutes: 10,
                catchUp: 'once',
            },
            retention: { intradayDays: 14, daily: 30, weekly: 0, monthly: 0, yearly: 0, keepLast: 3 },
            alerting: { maxAgeHours: 12 },
        },
    },
    {
        key: 'long-term-archive',
        name: 'Long-term archive',
        description: 'Explicit selected folders, daily at 22:00 local time, 30 daily, 12 weekly, and 12 monthly snapshots.',
        spec: {
            selection: { mode: 'paths', includePaths: [] },
            schedule: { kind: 'daily', times: ['22:00'], timezone: 'device', jitterMinutes: 20, catchUp: 'once' },
            retention: { intradayDays: 0, daily: 30, weekly: 12, monthly: 12, yearly: 0, keepLast: 3 },
            alerting: { maxAgeHours: 48 },
        },
    },
];
export function parsePolicySpec(input) {
    return policySpecSchema.parse(input);
}
//# sourceMappingURL=policy.js.map